import { apiRequest } from "../../lib/api-client";
import type {
  CompanySettings,
  DashboardAlerts,
  DashboardStats,
  DashboardBundle,
  DashboardSummary,
  VisitRow,
} from "./types";

// Company-wide users (ADMIN/HR) can narrow the dashboard to one site via the
// site switcher; scoped users pass undefined and the server locks them to
// their own site regardless.
function siteQuery(siteId?: number | null): string {
  return siteId != null ? `?site_id=${siteId}` : "";
}

export function getDashboardStats(signal?: AbortSignal, siteId?: number | null) {
  return apiRequest<DashboardStats>(`/dashboard/stats${siteQuery(siteId)}`, { signal });
}

export function getTodayVisits(signal?: AbortSignal, siteId?: number | null) {
  return apiRequest<VisitRow[]>(`/dashboard/today${siteQuery(siteId)}`, { signal });
}

export function getRecentVisitors(signal?: AbortSignal, siteId?: number | null) {
  return apiRequest<VisitRow[]>(`/dashboard/recent-visitors${siteQuery(siteId)}`, { signal });
}

export function getPendingApprovals(signal?: AbortSignal, siteId?: number | null) {
  return apiRequest<VisitRow[]>(`/dashboard/pending-approvals${siteQuery(siteId)}`, { signal });
}

export function getDashboardAlerts(signal?: AbortSignal, siteId?: number | null) {
  return apiRequest<DashboardAlerts>(`/dashboard/alerts${siteQuery(siteId)}`, { signal });
}

export function getDashboardSummary(signal?: AbortSignal, siteId?: number | null) {
  return apiRequest<DashboardSummary>(`/dashboard/summary${siteQuery(siteId)}`, { signal });
}

export function getWidgetPrefs(signal?: AbortSignal) {
  return apiRequest<{ widget_ids: string[] }>("/dashboard/widget-prefs", { signal });
}

export function updateWidgetPrefs(widgetIds: string[]) {
  return apiRequest<{ widget_ids: string[] }>("/dashboard/widget-prefs", {
    method: "PUT",
    body: { widget_ids: widgetIds },
  });
}

type VisitTransitionResponse = {
  visit_id: number;
  status: string;
  checkin_time?: string;
  checkout_time?: string;
};

type ApprovalTransitionResponse = {
  visit_id: number;
  status: string;
};

type CheckInPayload = {
  full_name: string;
  host_id: number;
  purpose: string;
  phone?: string;
  email?: string;
  company?: string;
  expected_exit?: string;
  notes?: string;
  has_laptop?: boolean;
  laptop_mac_address?: string;
  laptop_serial_number?: string;
  // Only honored for a company-wide operator; a site-scoped operator's
  // walk-ins land on their own site regardless (server-enforced).
  site_id?: number | null;
  // ID verification (security gate). id_verified: true if the scanned ID
  // passed local validation, false if captured-but-unverified, null if none.
  // entry_decision only matters when id_verified is false: "reverify" flags
  // the visit for a gate re-check, "allow_in" overrides.
  id_verified?: boolean | null;
  entry_decision?: "reverify" | "allow_in";
};

export type ScanIdResult = {
  id_type: string | null;
  id_number: string | null;
  full_name: string | null;
  valid: boolean;
  validation_message: string;
};

export function scanVisitorId(image: File) {
  const form = new FormData();
  form.set("id_image", image);
  return apiRequest<ScanIdResult>("/visitors/scan-id", {
    method: "POST",
    body: form,
  });
}

type CheckInResponse = {
  visit_id: number;
  visitor_id: number;
  badge_code: string;
  status: string;
};

export function checkInVisitor(payload: CheckInPayload) {
  return apiRequest<CheckInResponse>("/visits/check-in", {
    method: "POST",
    body: payload,
  });
}

export function uploadVisitorPhoto(visitorId: number, file: File) {
  const form = new FormData();
  form.set("file", file);
  return apiRequest<{ id: number }>(`/visitors/${visitorId}/photo`, {
    method: "POST",
    body: form,
  });
}

export function uploadVisitorIdDocument(visitorId: number, file: File) {
  const form = new FormData();
  form.set("file", file);
  return apiRequest<{ id: number }>(`/visitors/${visitorId}/id-document`, {
    method: "POST",
    body: form,
  });
}

export function setVisitorIdNumber(visitorId: number, idType: string, idNumber: string) {
  return apiRequest<{ id: number }>(`/visitors/${visitorId}/id-number`, {
    method: "POST",
    body: { id_type: idType, id_number: idNumber },
  });
}

type BadgeLookupResponse = {
  visit_id: number;
  status: string;
  visitor_name: string;
  host_name: string;
  id_verified?: boolean | null;
  gate_reverify_required?: boolean;
};

export function lookupVisitByBadge(badgeCode: string) {
  return apiRequest<BadgeLookupResponse>(`/visits/by-badge/${encodeURIComponent(badgeCode)}`);
}

type PassEnterResponse = {
  visit_id: number;
  visitor_name: string;
  status: string;
  pass_id: number;
};

// Reusable long-term pass entry: scanning a pass badge records a fresh INSIDE
// visit for today (no per-visit approval). Used as the gate's fallback when a
// scanned badge isn't a single-visit badge.
export function enterWithPass(badgeCode: string) {
  return apiRequest<PassEnterResponse>(`/passes/enter/${encodeURIComponent(badgeCode)}`, {
    method: "POST",
  });
}

export type MyRecurringPass = {
  id: number;
  visitor_name: string;
  host_name?: string | null;
  purpose: string;
  pass_type: string;
  valid_from: string;
  valid_until?: string | null;
  badge_code?: string | null;
  pass_url?: string | null;
  status: string;
  created_at?: string | null;
};

export type RequestPassPayload = {
  full_name: string;
  purpose: string;
  pass_type: "permanent" | "monthly" | "yearly" | "custom";
  valid_from?: string | null;
  valid_until?: string | null;
  phone?: string | null;
  email?: string | null;
};

export function listMyRecurringPasses(signal?: AbortSignal) {
  return apiRequest<MyRecurringPass[]>("/passes/mine", { signal });
}

export function requestRecurringPass(payload: RequestPassPayload) {
  return apiRequest<MyRecurringPass>("/passes/request", { method: "POST", body: payload });
}

// Role-open host directory: unlike /admin/hosts (ADMIN/HR only), this is
// readable by SECURITY and HOST users too, so the gate check-in form and
// invite form work for the roles that actually use them.
export type HostDirectoryEntry = {
  id: number;
  name: string;
  department?: string | null;
  username?: string | null;
};

export function listHostsDirectory(signal?: AbortSignal) {
  return apiRequest<HostDirectoryEntry[]>("/hosts", { signal });
}

export function enterVisitor(visitId: number) {
  return apiRequest<VisitTransitionResponse>("/visits/enter", {
    method: "POST",
    body: { visit_id: visitId },
  });
}

export function checkoutVisitor(visitId: number) {
  return apiRequest<VisitTransitionResponse>("/visits/check-out", {
    method: "POST",
    body: { visit_id: visitId },
  });
}

export function approvePendingVisit(visitId: number) {
  return apiRequest<ApprovalTransitionResponse>(`/approvals/approve/${visitId}`, {
    method: "POST",
  });
}

export function rejectPendingVisit(visitId: number) {
  return apiRequest<ApprovalTransitionResponse>(`/approvals/reject/${visitId}`, {
    method: "POST",
  });
}

export function denyVisitor(visitId: number) {
  return apiRequest<ApprovalTransitionResponse>(`/approvals/deny/${visitId}`, {
    method: "POST",
  });
}

type OverstayDetectionResponse = {
  overstays_marked: number;
};

export function runOverstayDetection() {
  return apiRequest<OverstayDetectionResponse>("/admin/detect-overstay", {
    method: "POST",
  });
}

export function getApprovedVisits(signal?: AbortSignal, siteId?: number | null) {
  return apiRequest<VisitRow[]>(`/dashboard/approved${siteQuery(siteId)}`, { signal });
}

export function getCompanySettings(signal?: AbortSignal) {
  return apiRequest<CompanySettings>("/dashboard/company-settings", { signal });
}

export async function getDashboardBundle(
  signal?: AbortSignal,
  siteId?: number | null,
): Promise<DashboardBundle> {
  const [
    stats,
    summary,
    todayVisits,
    recentVisitors,
    pendingApprovals,
    approvedVisits,
    alerts,
    companySettings,
  ] = await Promise.all([
    getDashboardStats(signal, siteId),
    getDashboardSummary(signal, siteId),
    getTodayVisits(signal, siteId),
    getRecentVisitors(signal, siteId),
    getPendingApprovals(signal, siteId),
    getApprovedVisits(signal, siteId),
    getDashboardAlerts(signal, siteId),
    getCompanySettings(signal),
  ]);

  return {
    stats,
    summary,
    todayVisits,
    recentVisitors,
    pendingApprovals,
    approvedVisits,
    alerts,
    companySettings,
  };
}
